import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { ShieldCheck, Upload, CheckCircle2, XCircle } from "lucide-react";

export default function Verify() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [photoB64, setPhotoB64] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [bio, setBio] = useState(user?.bio || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("JPEG, PNG, or WEBP only.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Please keep file size under 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setPhotoPreview(dataUrl);
      setPhotoB64(dataUrl);
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!photoB64) { toast.error("A clear photograph is required."); return; }
    if (!bio || bio.length < 10) { toast.error("Please write a bio of at least 10 characters."); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/verify", { selfie_base64: photoB64, bio });
      setResult(data);
      await refresh();
      if (data.approved) {
        toast.success("Verified.");
      } else {
        toast.error(data.verdict || "Not yet approved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification could not complete.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F5F5F5]">
      <Navbar />

      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 goji-glass mb-6">
            <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="font-label text-[0.6rem] text-[#D4AF37]">The Verification</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-normal" data-testid="verify-title">
            A considered <span className="italic text-[#D4AF37]">review.</span>
          </h1>
          <p className="mt-5 text-[#F5F5F5]/65 max-w-xl mx-auto">
            Please provide a clear, recent photograph and a written introduction. The reviewer will assess presence, intent and authenticity.
          </p>
        </div>

        <div className="goji-glass p-8 sm:p-10" data-testid="verify-form">
          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <label className="font-label text-[0.6rem] text-[#D4AF37] mb-3 block">Your photograph</label>
              <div
                onClick={() => fileRef.current?.click()}
                className="aspect-square border border-dashed border-[#D4AF37]/35 flex items-center justify-center cursor-pointer hover:border-[#D4AF37] transition-colors overflow-hidden"
                data-testid="verify-upload-zone"
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-[#F5F5F5]/55 p-6">
                    <Upload className="w-7 h-7 mx-auto mb-3 text-[#D4AF37]" />
                    <p className="text-sm">Upload</p>
                    <p className="font-label text-[0.55rem] text-[#F5F5F5]/40 mt-2">JPEG / PNG / WEBP · under 5MB</p>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onFile} data-testid="verify-file-input" />
            </div>

            <div className="flex flex-col">
              <label className="font-label text-[0.6rem] text-[#D4AF37] mb-3 block">Your introduction</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={10}
                placeholder="Tell us who you are."
                className="flex-1 px-4 py-3 text-sm"
                data-testid="verify-bio-input"
              />
            </div>
          </div>

          <button onClick={submit} disabled={loading} className="mt-10 w-full btn-ghost" data-testid="verify-submit-button">
            {loading ? "Reviewing..." : "Submit for review"}
          </button>
        </div>

        {result && (
          <div className={`mt-8 goji-glass p-8 ${result.approved ? "border-[#D4AF37]/45" : "border-[#D4AF37]/20"}`} data-testid="verify-result">
            <div className="flex items-start gap-4">
              {result.approved ? (
                <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              ) : (
                <XCircle className="w-6 h-6 text-[#F5F5F5]/55 flex-shrink-0 mt-1" />
              )}
              <div className="flex-1">
                <p className="font-display text-2xl italic text-[#F5F5F5]">{result.verdict}</p>
                <p className="mt-3 text-[#F5F5F5]/75 text-sm leading-relaxed">{result.feedback}</p>
                <div className="mt-5 flex items-center gap-4">
                  <span className="font-label text-[0.6rem] text-[#D4AF37]">Score</span>
                  <div className="flex-1 h-px bg-[#D4AF37]/15 relative">
                    <div className="absolute inset-y-0 left-0 bg-[#D4AF37]" style={{ width: `${result.score || 0}%`, height: "1px", top: "0" }} />
                    <div className="absolute -top-px left-0 bg-[#D4AF37] h-0.5" style={{ width: `${result.score || 0}%` }} />
                  </div>
                  <span className="font-display text-base text-[#F5F5F5]">{result.score}</span>
                </div>
              </div>
            </div>
            {result.approved && (
              <button onClick={() => navigate("/dashboard")} className="mt-8 btn-ghost w-full" data-testid="verify-continue-button">
                Enter the circle
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
